import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ConsultationSchedulerModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1=date+time, 2=form, 3=confirmation
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '', projectDetails: '' });
  const [formErrors, setFormErrors] = useState({});

  // Calendar state
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  // Fetch settings on mount
  useEffect(() => {
    if (isOpen) {
      fetch(`/api/scheduler/settings`)
        .then(r => r.json())
        .then(setSettings)
        .catch(() => {});
    }
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDate(null);
      setSelectedTime(null);
      setAvailableSlots([]);
      setFormData({ fullName: '', email: '', phone: '', projectDetails: '' });
      setFormErrors({});
      setBookingResult(null);
      setViewMonth(today.getMonth());
      setViewYear(today.getFullYear());
    }
  }, [isOpen]);

  // Fetch slots when date selected
  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = formatDateStr(selectedDate);
    setSlotsLoading(true);
    fetch(`/api/scheduler/slots?target_date=${dateStr}`)
      .then(r => r.json())
      .then(data => setAvailableSlots((data.slots || []).filter(s => s.available).map(s => s.time)))
      .catch(() => setAvailableSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate]);

  const handleEscKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscKey]);

  const formatDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatDisplayDate = (d) => {
    return `${DAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  const formatTime12h = (t) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  // Calendar helpers
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const isDateDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (d < todayStart) return true;
    if (!settings) return false;
    // Python weekday: Mon=0. JS getDay: Sun=0. Convert JS to Python: (getDay()+6)%7
    const pyWeekday = (d.getDay() + 6) % 7;
    if (!settings.workingDays.includes(pyWeekday)) return true;
    const dateStr = formatDateStr(d);
    if (settings.blockedDates && settings.blockedDates.includes(dateStr)) return true;
    return false;
  };

  const isDateSelected = (day) => {
    if (!selectedDate) return false;
    return selectedDate.getDate() === day && selectedDate.getMonth() === viewMonth && selectedDate.getFullYear() === viewYear;
  };

  const handleDateClick = (day) => {
    if (isDateDisabled(day)) return;
    setSelectedDate(new Date(viewYear, viewMonth, day));
    setSelectedTime(null);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const canGoPrev = () => {
    const prev = viewMonth === 0 ? new Date(viewYear - 1, 11, 1) : new Date(viewYear, viewMonth - 1, 1);
    const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return prev >= todayMonth;
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.fullName.trim()) errors.fullName = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email address';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/scheduler/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          date: formatDateStr(selectedDate),
          time: selectedTime
        })
      });
      const data = await res.json();
      setBookingResult(data);
      if (data.status === 'confirmed') setStep(3);
      else setFormErrors({ submit: data.message });
    } catch {
      setFormErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Render calendar grid
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(viewMonth, viewYear);
    const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
    const cells = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const disabled = isDateDisabled(day);
      const selected = isDateSelected(day);
      const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

      cells.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          disabled={disabled}
          data-testid={`calendar-day-${day}`}
          className={`
            w-10 h-10 rounded-lg text-sm font-medium transition-all
            ${disabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-primary/10 cursor-pointer'}
            ${selected ? 'bg-primary text-white hover:bg-primary/90' : ''}
            ${isToday && !selected ? 'ring-1 ring-primary text-primary font-bold' : ''}
          `}
        >
          {day}
        </button>
      );
    }
    return cells;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-testid="scheduler-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
          data-testid="scheduler-modal"
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            data-testid="scheduler-modal-close"
            aria-label="Close scheduler"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {/* Header */}
          <div className="p-6 pb-4 border-b border-gray-100">
            <h2 className="text-2xl font-heading font-bold text-gray-900 pr-10">
              {step === 1 && 'Select a Date & Time'}
              {step === 2 && 'Complete Your Booking'}
              {step === 3 && 'Consultation Confirmed'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {step === 1 && 'Free 15-minute consultation with a flooring expert'}
              {step === 2 && `${formatDisplayDate(selectedDate)} at ${formatTime12h(selectedTime)}`}
              {step === 3 && 'Your appointment details are below'}
            </p>
            {step === 1 && (
              <p className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                All times shown in Eastern Time (ET)
              </p>
            )}
            {/* Step indicator */}
            <div className="flex gap-2 mt-4">
              {[1, 2, 3].map(s => (
                <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-gray-200'}`} />
              ))}
            </div>
          </div>

          {/* Step 1: Date & Time */}
          {step === 1 && (
            <div className="p-6">
              {/* Calendar */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={prevMonth}
                    disabled={!canGoPrev()}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="calendar-prev-month"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="font-heading font-semibold text-gray-900">
                    {MONTHS[viewMonth]} {viewYear}
                  </span>
                  <button
                    onClick={nextMonth}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid="calendar-next-month"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DAYS.map(d => (
                    <div key={d} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
                  ))}
                </div>

                {/* Day cells */}
                <div className="grid grid-cols-7 gap-1 place-items-center">
                  {renderCalendar()}
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div data-testid="time-slots-section">
                  <h3 className="font-heading font-semibold text-sm text-gray-700 mb-3">
                    Available Times — {formatDisplayDate(selectedDate)}
                  </h3>

                  {slotsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">No available slots for this date. Please choose another day.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          onClick={() => setSelectedTime(slot)}
                          data-testid={`time-slot-${slot}`}
                          className={`
                            flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all
                            ${selectedTime === slot
                              ? 'bg-primary text-white border-primary'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary'
                            }
                          `}
                        >
                          <Clock className="w-3.5 h-3.5" />
                          {formatTime12h(slot)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Continue button */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!selectedDate || !selectedTime}
                  className="w-full"
                  data-testid="scheduler-continue-btn"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Booking Form */}
          {step === 2 && (
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="sched-fullName">Full Name *</Label>
                  <Input
                    id="sched-fullName"
                    value={formData.fullName}
                    onChange={e => setFormData(p => ({ ...p, fullName: e.target.value }))}
                    placeholder="John Smith"
                    data-testid="scheduler-fullname"
                    className={formErrors.fullName ? 'border-red-400' : ''}
                  />
                  {formErrors.fullName && <p className="text-xs text-red-500 mt-1">{formErrors.fullName}</p>}
                </div>
                <div>
                  <Label htmlFor="sched-email">Email *</Label>
                  <Input
                    id="sched-email"
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    placeholder="john@company.com"
                    data-testid="scheduler-email"
                    className={formErrors.email ? 'border-red-400' : ''}
                  />
                  {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="sched-phone">Phone</Label>
                  <Input
                    id="sched-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    data-testid="scheduler-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="sched-details">Project Details (optional)</Label>
                  <Textarea
                    id="sched-details"
                    value={formData.projectDetails}
                    onChange={e => setFormData(p => ({ ...p, projectDetails: e.target.value }))}
                    placeholder="Tell us about your flooring project..."
                    rows={3}
                    data-testid="scheduler-details"
                  />
                </div>

                {formErrors.submit && (
                  <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg" data-testid="scheduler-error">{formErrors.submit}</p>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-100 flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="scheduler-back-btn">
                  Back
                </Button>
                <Button onClick={handleSubmit} disabled={submitting} className="flex-1" data-testid="scheduler-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">You're All Set!</h3>
              <p className="text-gray-600 mb-6">
                Your 15-minute consultation has been confirmed.
              </p>

              <div className="bg-gray-50 rounded-xl p-5 text-left space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Date</span>
                  <span className="text-sm font-medium text-gray-900">{selectedDate && formatDisplayDate(selectedDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Time</span>
                  <span className="text-sm font-medium text-gray-900">{selectedTime && formatTime12h(selectedTime)} ET</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Duration</span>
                  <span className="text-sm font-medium text-gray-900">15 minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Confirmation</span>
                  <span className="text-sm font-medium text-gray-900">{bookingResult?.id?.slice(0, 8).toUpperCase()}</span>
                </div>
              </div>

              <p className="text-xs text-gray-400 mb-6">
                A member of our team will reach out to you at the scheduled time. If you need to make changes, please contact us directly.
              </p>

              <Button onClick={onClose} className="w-full" data-testid="scheduler-done-btn">
                Done
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConsultationSchedulerModal;
